import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { readAxiosV2Error } from "../../lib/api-v2/errors";
import { getDefaultAuthenticatedRoute } from "../../lib/userAccess";
import { describeAcceptError, PENDING_OUTCOME, type AcceptOutcome } from "./accept-outcome";
import {
  authPathForInvite,
  clearStashedInviteToken,
  INVITE_ACCEPT_PATH,
  peekStashedInviteToken,
  readInviteToken,
  stashInviteToken,
} from "./accept-token";
import { acceptInvitation } from "./invitations-api";

/** 202 means acceptance is recorded but activation is unfinished; it converges quickly. */
export const ACCEPT_RETRY_DELAY_MS = 1500;
export const ACCEPT_MAX_ATTEMPTS = 5;

type Phase = { kind: "working" } | { kind: "signed_out" } | { kind: "missing" } | { kind: "error"; outcome: AcceptOutcome };

export function AcceptInvitationPage() {
  const { isAuthenticated, loading, token, adoptSession, logout } = useAuth();
  const navigate = useNavigate();
  const [inviteToken] = useState(() => {
    const fromLink = readInviteToken(window.location.hash);
    if (fromLink) stashInviteToken(fromLink);
    return fromLink || peekStashedInviteToken();
  });
  const [phase, setPhase] = useState<Phase>({ kind: "working" });
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Drop the token from the address bar once it is parked.
  useEffect(() => {
    if (window.location.hash) navigate(INVITE_ACCEPT_PATH, { replace: true });
  }, [navigate]);

  const run = useCallback(async (attempt: number) => {
    if (!inviteToken || !token) return;
    let outcome: AcceptOutcome;
    try {
      const result = await acceptInvitation(inviteToken, token);
      if (result.status === "accepted" && result.access_token) {
        clearStashedInviteToken();
        const user = await adoptSession(result.access_token);
        if (mountedRef.current) navigate(getDefaultAuthenticatedRoute(user), { replace: true });
        return;
      }
      outcome = PENDING_OUTCOME;
    } catch (error) {
      outcome = describeAcceptError(readAxiosV2Error(error).code);
    }
    if (!mountedRef.current) return;
    if (outcome.retry && attempt + 1 < ACCEPT_MAX_ATTEMPTS) {
      window.setTimeout(() => { if (mountedRef.current) void run(attempt + 1); }, ACCEPT_RETRY_DELAY_MS);
      return;
    }
    if (!outcome.keepToken) clearStashedInviteToken();
    setPhase({ kind: "error", outcome });
  }, [adoptSession, inviteToken, navigate, token]);

  useEffect(() => {
    if (loading) return;
    if (!inviteToken) { setPhase({ kind: "missing" }); return; }
    if (!isAuthenticated) { setPhase({ kind: "signed_out" }); return; }
    if (startedRef.current) return;
    startedRef.current = true;
    setPhase({ kind: "working" });
    void run(0);
  }, [inviteToken, isAuthenticated, loading, run]);

  const retry = () => {
    setPhase({ kind: "working" });
    void run(0);
  };

  const signInAsSomeoneElse = () => {
    logout();
    navigate(authPathForInvite("/login"), { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <div className="w-full max-w-md rounded-2xl border border-line bg-elevated p-8 text-center shadow-sm">
        <MailCheck className="mx-auto h-8 w-8 text-brand" />
        <h1 className="mt-3 text-xl font-bold text-ink">Organization invitation</h1>

        {phase.kind === "working" && (
          <p className="mt-4 flex items-center justify-center gap-2 text-sm text-ink-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Accepting your invitation…
          </p>
        )}

        {phase.kind === "missing" && (
          <p className="mt-4 text-sm text-ink-2">
            This page needs the link from your invitation. Open the invite link again.
          </p>
        )}

        {phase.kind === "signed_out" && (
          <>
            <p className="mt-4 text-sm text-ink-2">
              Sign in or create an account with the address the invite was sent to.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => navigate(authPathForInvite("/login"))}
                className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover"
              >
                Sign in
              </button>
              <button
                onClick={() => navigate(authPathForInvite("/register"))}
                className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft"
              >
                Create account
              </button>
            </div>
          </>
        )}

        {phase.kind === "error" && (
          <>
            <p role="alert" className="mt-4 text-sm text-ink-2">{phase.outcome.message}</p>
            <div className="mt-6 flex justify-center gap-3">
              {phase.outcome.action === "retry" && (
                <button onClick={retry} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
                  Try again
                </button>
              )}
              {phase.outcome.action === "sign_in_other" && (
                <button onClick={signInAsSomeoneElse} className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
                  Sign in with another account
                </button>
              )}
              <button
                onClick={() => navigate("/", { replace: true })}
                className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft"
              >
                Go to dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
